/* @flow */
import * as React from 'react';

import assertions from 'lib/assertions';
import AdminTeacherTypeahead from 'admin/components/AdminTeacherTypeahead';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Select from 'components/Select';
import ToastActions from 'actions/ToastActions';

type Props = {
  organizations: Array<*>,
  onRequestHide: () => void,
  addAdmin: (
    organizationId: ?Backbone$Id,
    userUuid: ?string,
    districtId: ?Backbone$Id
  ) => Promise<*>,
  directAddAdmin: (
    organizationId: ?Backbone$Id,
    name: ?string,
    email: ?string,
    districtId: ?number
  ) => Promise<*>,
  districtId: ?Backbone$Id,
};

type State = {
  organization: ?Object,
  teacher?: ?Object,
  directAddName?: ?string,
  directAddEmail?: ?string,
  formValid: boolean,
};

class AdminAddAdminForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { organizations } = props;

    this.state = {
      organization: organizations.length === 1 ? organizations[0] : undefined,
      formValid: false,
    };
  }

  formIsValid = () =>
    this.state.organization &&
    (this.state.teacher || (this.state.directAddName && this.state.directAddEmail));

  getOrgOptions = () => {
    const { organizations } = this.props;
    const options: Array<any> = organizations.map(org => {
      return { value: org.id, label: org.name };
    });

    return options;
  };

  handleOrgSelect = (orgId: String) => {
    this.setState({
      organization: this.props.organizations.find(o => o.id.toString() === orgId),
      directAddName: undefined,
      teacher: undefined,
    });
  };

  handleTeacherSelect = (user: *) => {
    this.setState({ teacher: user });
  };

  handleTeacherCancel = () => {
    this.setState({ teacher: undefined });
  };

  setDirectAdd = (name: string) => {
    this.setState({ directAddName: name });
  };

  renderDirectAdd = () => {
    return (
      <div data-test="direct-add-fields">
        <FormElement
          data-test="direct-add-name-field"
          label="Full name"
          onValidChange={e => this.setState({ directAddName: e.target.value })}
          element={Input}
          elementProps={{
            required: true,
            name: 'full_name',
            value: this.state.directAddName,
          }}
        />
        <FormElement
          data-test="direct-add-email-field"
          validate={e => {
            assertions.email(e.target.value);
          }}
          label="School email"
          onValidChange={e => this.setState({ directAddEmail: e.target.value })}
          onInvalidChange={() => this.setState({ directAddEmail: undefined })}
          element={Input}
          elementProps={{
            required: true,
            type: 'email',
            placeholder: 'name@school.edu',
            name: 'email',
          }}
        />
      </div>
    );
  };

  renderUserTypeAhead = () => {
    if (!this.state.organization) {
      return null;
    }

    return (
      <AdminTeacherTypeahead
        organizationId={this.state.organization.id}
        onSelect={this.handleTeacherSelect}
        onDismissTeacher={this.handleTeacherCancel}
        onDirectAdd={this.setDirectAdd}
      />
    );
  };

  handleResponse = (response: *) => {
    if (response.admin) {
      // If admin was successfully added, show a success toast with text based on whether the admin
      // is in a pending state or not
      const { name } = response.admin.user;
      const toastText =
        response.admin.adminState === 'pending'
          ? `${name} will be added as an admin once they confirm their account.`
          : `${name} has been added as an admin.`;
      ToastActions.showSuccessToast(toastText);
      this.props.onRequestHide();
      return;
    }

    if (response.error) {
      ToastActions.showGQLError(response.error);
      return;
    }

    // If user is already an admin, both response.admin and response.error will come back empty
    // We still want to show a toast
    const name =
      (this.state.teacher && this.state.teacher.name) ||
      this.state.directAddName ||
      'That individual';
    ToastActions.showSuccessToast(`${name} has already been added as an admin.`);
    this.props.onRequestHide();
  };

  handleError = (values: *, districtId: *, errorMessage: string) => {
    console.warn({ values, districtId });
    console.error(`AdminAddAdminForm - ${errorMessage}`);
    ToastActions.showErrorToast('Oops! Looks like something went wrong.');
  };

  handleFormValidSubmit = () => {
    const { teacher, directAddName, directAddEmail, organization } = this.state;
    const { districtId, addAdmin, directAddAdmin } = this.props;

    if (!organization) {
      console.error('AdminAddAdminForm - tried to submit without an organization selected');
      return;
    }

    if (teacher) {
      addAdmin(organization.id, teacher.uuid, districtId).then(
        res => this.handleResponse(res.data.addAdmin),
        () => {
          this.handleError(this.state, districtId, 'error adding admin');
        }
      );
    } else {
      directAddAdmin(organization.id, directAddName, directAddEmail, Number(districtId)).then(
        res => this.handleResponse(res.data.directAddAdmin),
        () => {
          this.handleError(this.state, districtId, 'error direct adding admin');
        }
      );
    }
  };

  render() {
    if (!this.props.organizations) {
      return null;
    }

    const orgs = this.getOrgOptions();

    const orgSelected = this.state && this.state.organization;

    const showSearch = orgSelected && !this.state.directAddName;
    const showDirectAdd = orgSelected && this.state.directAddName;

    return (
      <BoxBody divided>
        <form
          data-test="add-admin-form"
          onSubmit={e => {
            e.preventDefault();
            this.handleFormValidSubmit();
          }}>
          <FormElement
            data-test="org-field"
            label="Organization"
            onValidChange={this.handleOrgSelect}
            element={Select}
            elementProps={{
              name: 'orgId',
              options: orgs,
              value: this.state.organization ? this.state.organization.id : undefined,
              required: true,
              searchable: false,
            }}
          />
          {showSearch && this.renderUserTypeAhead()}
          {showDirectAdd && this.renderDirectAdd()}
          <Button type="submit" disabled={!this.formIsValid()}>
            Add admin
          </Button>
        </form>
      </BoxBody>
    );
  }
}

export default AdminAddAdminForm;
